import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Download,
  KeyRound,
  Loader2,
  LogOut,
  Mail,
  MessageCircle,
  MonitorSmartphone,
  Phone,
  ShieldCheck,
} from 'lucide-react';
import api from '../../utils/api';
import { downloadAccountData, saveAccountDataExport } from '../../utils/accountDataApi';
import LoginMethodBindingDialog from './LoginMethodBindingDialog';
import SessionReauthDialog from './SessionReauthDialog';

function formatSessionTime(value, english) {
  if (!value) return english ? 'Unknown' : '未知';
  try {
    return new Intl.DateTimeFormat(english ? 'en-US' : 'zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return english ? 'Unknown' : '未知';
  }
}

function formatDeviceType(value, english) {
  const labels = english
    ? { desktop: 'Desktop', mobile: 'Mobile', tablet: 'Tablet', tv: 'TV' }
    : { desktop: '桌面设备', mobile: '手机', tablet: '平板', tv: '电视' };
  return labels[value] || (english ? 'Device' : '设备');
}

function ConnectionCard({ icon: Icon, title, description, method, bound, display, preview, onBind, english }) {
  return (
    <article className={`account-connection-card${bound ? ' is-bound' : ''}`}>
      <span className="account-connection-card__icon"><Icon size={19} /></span>
      <div>
        <h3>{title}</h3>
        <p>{bound && display ? display : description}</p>
      </div>
      <span className={`account-status account-status--${bound ? 'ready' : 'warm'}`}>
        {bound ? (english ? 'Bound' : '已绑定') : (english ? 'Not bound' : '未绑定')}
      </span>
      <button className="account-button" type="button" disabled={bound || preview} onClick={() => onBind(method)}>
        {bound ? <CheckCircle2 size={14} /> : null}
        {bound ? (english ? 'Available for sign-in' : '可用于登录') : (english ? `Bind ${title}` : `绑定${title}`)}
      </button>
    </article>
  );
}

export default function SecuritySettingsPanel({
  user,
  refreshUser,
  preview = false,
  english = false,
  darkActive = false,
}) {
  const [activeMethod, setActiveMethod] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [sessionData, setSessionData] = useState({ sessions: [], other_session_count: 0, reauthenticated_until: null });
  const [sessionsLoading, setSessionsLoading] = useState(!preview);
  const [sessionsError, setSessionsError] = useState('');
  const [sessionNotice, setSessionNotice] = useState('');
  const [sessionBusy, setSessionBusy] = useState('');
  const [exportBusy, setExportBusy] = useState(false);
  const [exportFeedback, setExportFeedback] = useState({ type: '', message: '' });
  const [pendingProtectedAction, setPendingProtectedAction] = useState(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const loginMethods = user?.login_methods || {};
  const methods = useMemo(() => [
    {
      method: 'wechat',
      icon: MessageCircle,
      title: english ? 'WeChat' : '微信',
      description: english ? 'Scan the official QR code to add WeChat sign-in.' : '扫描官方二维码，添加微信登录方式。',
      ...loginMethods.wechat,
    },
    {
      method: 'phone',
      icon: Phone,
      title: english ? 'phone' : '手机号',
      description: english ? 'Verify an SMS code to add phone sign-in.' : '验证短信验证码，添加手机号登录方式。',
      ...loginMethods.phone,
    },
    {
      method: 'email',
      icon: Mail,
      title: english ? 'email' : '邮箱',
      description: english ? 'Verify an email code and set a sign-in password.' : '验证邮件验证码并设置登录密码。',
      ...loginMethods.email,
    },
  ], [english, loginMethods.email, loginMethods.phone, loginMethods.wechat]);
  const boundCount = methods.filter(method => method.bound).length;

  const loadSessions = useCallback(async () => {
    if (preview) {
      setSessionData({
        sessions: [{
          id: 'preview-current',
          current: true,
          browser: english ? 'Current browser' : '当前浏览器',
          os: english ? 'Preview' : '预览模式',
          device_type: 'desktop',
          login_ip: '203.0.113.*',
          created_at: new Date().toISOString(),
          last_seen_at: new Date().toISOString(),
        }],
        other_session_count: 0,
        reauthenticated_until: null,
      });
      return;
    }
    setSessionsLoading(true);
    setSessionsError('');
    try {
      const response = await api.get('/auth/sessions');
      setSessionData(response.data);
    } catch (error) {
      setSessionsError(getErrorMessage(error, '登录设备加载失败，请稍后重试'));
    } finally {
      setSessionsLoading(false);
    }
  }, [english, preview]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const openBinding = useCallback(method => {
    if (preview) return;
    setSuccessMessage('');
    setActiveMethod(method);
  }, [preview]);

  const handleBound = useCallback(async method => {
    if (typeof refreshUser === 'function') {
      try {
        await refreshUser();
      } catch {
        window.dispatchEvent(new Event('auth-changed'));
      }
    }
    const labels = {
      wechat: english ? 'WeChat' : '微信',
      phone: english ? 'Phone' : '手机号',
      email: english ? 'Email' : '邮箱',
    };
    setSuccessMessage(english ? `${labels[method]} is now available for sign-in.` : `${labels[method]}绑定成功，现可用于登录`);
  }, [english, refreshUser]);

  const runProtectedAction = useCallback(async action => {
    if (!action || preview) return;
    if (action.type === 'export') {
      setExportBusy(true);
      setExportFeedback({ type: '', message: '' });
      try {
        const result = await downloadAccountData();
        saveAccountDataExport(result);
        setExportFeedback({
          type: 'success',
          message: english ? 'Your Excel workbook has been downloaded.' : '账户数据 Excel 已下载',
        });
      } catch (error) {
        if (error.code === 'REAUTH_REQUIRED') {
          setPendingProtectedAction(action);
          setReauthOpen(true);
        } else {
          setExportFeedback({
            type: 'error',
            message: getErrorMessage(error, '账户数据导出失败，请稍后重试'),
          });
        }
      } finally {
        setExportBusy(false);
      }
      return;
    }

    const busyKey = action.type === 'others' ? 'others' : action.sessionId;
    setSessionBusy(busyKey);
    setSessionsError('');
    setSessionNotice('');
    try {
      if (action.type === 'others') {
        const response = await api.post('/auth/sessions/logout-others');
        const count = Number(response.data?.revoked_count) || 0;
        setSessionNotice(english ? `${count} other device(s) signed out.` : `已退出 ${count} 个其他登录设备`);
      } else {
        await api.delete(`/auth/sessions/${encodeURIComponent(action.sessionId)}`);
        setSessionNotice(english ? 'The device was signed out.' : '该设备已退出登录');
      }
      await loadSessions();
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === 'REAUTH_REQUIRED') {
        setPendingProtectedAction(action);
        setReauthOpen(true);
      } else {
        setSessionsError(getErrorMessage(error, '退出设备失败，请稍后重试'));
      }
    } finally {
      setSessionBusy('');
    }
  }, [english, loadSessions, preview]);

  const requestProtectedAction = useCallback(action => {
    if (preview) return;
    const verifiedUntil = Date.parse(sessionData.reauthenticated_until || '');
    if (Number.isFinite(verifiedUntil) && verifiedUntil > Date.now()) {
      void runProtectedAction(action);
      return;
    }
    setPendingProtectedAction(action);
    setReauthOpen(true);
  }, [preview, runProtectedAction, sessionData.reauthenticated_until]);

  const handleReauthenticated = useCallback(async reauthenticatedUntil => {
    const action = pendingProtectedAction;
    setSessionData(current => ({ ...current, reauthenticated_until: reauthenticatedUntil || current.reauthenticated_until }));
    setReauthOpen(false);
    setPendingProtectedAction(null);
    if (action) await runProtectedAction(action);
  }, [pendingProtectedAction, runProtectedAction]);

  return (
    <>
      <section className="account-page-heading">
        <div>
          <span className="account-eyebrow">SIGN-IN & SECURITY</span>
          <h1>{english ? 'Sign-in & Security' : '登录与安全'}</h1>
          <p>{english ? 'Add WeChat, phone, or email sign-in to the current account.' : '为当前账户添加微信、手机号或邮箱登录方式。'}</p>
        </div>
        <span className={`account-status account-status--${boundCount > 0 ? 'ready' : 'warm'}`}>
          {english ? `${boundCount} of 3 bound` : `已绑定 ${boundCount}/3`}
        </span>
      </section>

      {successMessage ? (
        <div className="account-inline-notice account-security-success" role="status">
          <CheckCircle2 size={18} />
          <div><strong>{english ? 'Sign-in method updated' : '登录方式已更新'}</strong><span>{successMessage}</span></div>
        </div>
      ) : null}

      <section className="account-settings-card">
        <div className="account-settings-card__heading">
          <div>
            <h2>{english ? 'Sign-in methods' : '登录方式'}</h2>
            <p>{english ? 'Only first-time binding is supported. Bound identities cannot be changed or removed here.' : '当前仅支持首次绑定，已绑定的身份不能在此换绑或解绑。'}</p>
          </div>
          <KeyRound size={17} />
        </div>
        <div className="account-connections">
          {methods.map(method => <ConnectionCard key={method.method} {...method} preview={preview} onBind={openBinding} english={english} />)}
        </div>
      </section>

      <section className="account-settings-card">
        <div className="account-settings-card__heading">
          <div>
            <h2>{english ? 'Signed-in devices' : '登录设备'}</h2>
            <p>{english ? 'Review recent devices and sign out sessions you no longer use.' : '查看最近登录设备，并退出不再使用的会话。'}</p>
          </div>
          <MonitorSmartphone size={17} />
        </div>
        <div className="account-session-list">
          {sessionsLoading ? (
            <div className="account-session-list__empty"><Loader2 className="account-spin" size={16} />{english ? 'Loading signed-in devices…' : '正在加载登录设备…'}</div>
          ) : sessionData.sessions?.length ? sessionData.sessions.map(session => (
            <article className="account-session" key={session.id}>
              <span className="account-session__device"><MonitorSmartphone size={18} /></span>
              <div>
                <h3>{session.browser} · {session.os} · {formatDeviceType(session.device_type, english)}</h3>
                <p>
                  {english ? 'Signed in' : '登录'} {formatSessionTime(session.created_at, english)}
                  {' · '}{english ? 'Active' : '活跃'} {formatSessionTime(session.last_seen_at, english)}
                  {' · IP '}{session.login_ip || (english ? 'Unknown' : '未知')}
                </p>
              </div>
              {session.current ? (
                <span className="account-status account-status--ready">{english ? 'Current device' : '当前设备'}</span>
              ) : (
                <button
                  className="account-button"
                  type="button"
                  disabled={Boolean(sessionBusy)}
                  onClick={() => requestProtectedAction({ type: 'single', sessionId: session.id })}
                >
                  {sessionBusy === session.id && <Loader2 className="account-spin" size={13} />}
                  {english ? 'Sign out' : '退出设备'}
                </button>
              )}
            </article>
          )) : (
            <div className="account-session-list__empty">{english ? 'No active signed-in devices.' : '暂无有效登录设备'}</div>
          )}
        </div>
        {sessionsError ? <div className="account-binding-feedback is-error" role="alert"><span>{sessionsError}</span><button type="button" onClick={loadSessions}>{english ? 'Retry' : '重试'}</button></div> : null}
        {sessionNotice ? <div className="account-binding-feedback is-success" role="status"><CheckCircle2 size={14} /><span>{sessionNotice}</span></div> : null}
      </section>

      <section className="account-settings-card">
        <div className="account-settings-card__heading">
          <div>
            <h2>{english ? 'Account data' : '账户数据'}</h2>
            <p>{english ? 'Export requires secondary verification. Before account deletion, team ownership and active orders must be resolved separately.' : '导出需要二次验证；账户注销前还需另行处理团队所有权和进行中订单。'}</p>
          </div>
          <ShieldCheck size={17} />
        </div>
        <div className="account-security-actions">
          <article>
            <span><Download size={17} /></span>
            <div><h3>{english ? 'Export account data' : '导出账户数据'}</h3><p>{english ? 'Package your profile, project list, asset list, and exportable creative content.' : '打包个人资料、项目清单、资产清单和可导出的创作内容。'}</p></div>
            <button
              className="account-button"
              type="button"
              disabled={preview || exportBusy}
              onClick={() => requestProtectedAction({ type: 'export' })}
            >
              {exportBusy && <Loader2 className="account-spin" size={13} />}
              {exportBusy ? (english ? 'Generating…' : '正在生成…') : (english ? 'Export Excel' : '导出 Excel')}
            </button>
          </article>
          <article>
            <span><LogOut size={17} /></span>
            <div><h3>{english ? 'Sign out other devices' : '退出其他设备'}</h3><p>{english ? 'Keep this browser signed in and end all other sessions.' : '保留当前浏览器，结束其他所有登录会话。'}</p></div>
            <button
              className="account-button"
              type="button"
              disabled={preview || sessionsLoading || Boolean(sessionBusy) || sessionData.other_session_count < 1}
              onClick={() => requestProtectedAction({ type: 'others' })}
            >
              {sessionBusy === 'others' && <Loader2 className="account-spin" size={13} />}
              {english ? 'Sign out others' : '退出其他设备'}
            </button>
          </article>
        </div>
        {exportFeedback.message ? (
          <div className={`account-binding-feedback is-${exportFeedback.type}`} role={exportFeedback.type === 'error' ? 'alert' : 'status'}>
            {exportFeedback.type === 'success' ? <CheckCircle2 size={14} /> : null}
            <span>{exportFeedback.message}</span>
          </div>
        ) : null}
      </section>

      {/*<section className="account-danger-zone">*/}
      {/*  <div>*/}
      {/*    <span><AlertTriangle size={16} /></span>*/}
      {/*    <div><h2>{english ? 'Delete account' : '注销账户'}</h2><p>{english ? 'Transfer teams and resolve orders and review tasks first. Asset deletion rules will be explained before confirmation.' : '注销前必须完成团队转让、订单和审核任务处理。资产删除规则会在确认页逐项说明。'}</p></div>*/}
      {/*  </div>*/}
      {/*  <button type="button" disabled><Trash2 size={14} />{english ? 'Request deletion' : '申请注销'}</button>*/}
      {/*</section>*/}

      {activeMethod ? (
        <LoginMethodBindingDialog
          method={activeMethod}
          onClose={() => setActiveMethod('')}
          onBound={handleBound}
          english={english}
          darkActive={darkActive}
        />
      ) : null}

      {reauthOpen ? (
        <SessionReauthDialog
          loginMethods={loginMethods}
          onClose={() => { setReauthOpen(false); setPendingProtectedAction(null); }}
          onVerified={handleReauthenticated}
          english={english}
          darkActive={darkActive}
        />
      ) : null}
    </>
  );
}
